import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { LogOut, ExternalLink } from "lucide-react";
import { LeadsTab } from "@/components/admin/LeadsTab";
import { ContentTab } from "@/components/admin/ContentTab";
import { ServicesTab } from "@/components/admin/ServicesTab";
import { GalleryTab } from "@/components/admin/GalleryTab";
import { TestimonialsTab } from "@/components/admin/TestimonialsTab";
import { NavigationTab } from "@/components/admin/NavigationTab";
import { toast } from "sonner";

export default function AdminDashboard() {
  const nav = useNavigate();
  const [authorized, setAuthorized] = useState<boolean | null>(null);
  const [email, setEmail] = useState<string>("");

  useEffect(() => {
    document.title = "Admin Dashboard — Gurpreet Refrigeration";
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      if (!session) { nav("/admin/auth", { replace: true }); return; }
      setEmail(session.user.email || "");
      supabase.rpc("has_role", { _user_id: session.user.id, _role: "admin" }).then(({ data }) => {
        if (!data) { toast.error("Not authorized as admin"); supabase.auth.signOut(); nav("/admin/auth", { replace: true }); }
        else setAuthorized(true);
      });
    });
    supabase.auth.getSession().then(({ data }) => {
      if (!data.session) nav("/admin/auth", { replace: true });
      else {
        setEmail(data.session.user.email || "");
        supabase.rpc("has_role", { _user_id: data.session.user.id, _role: "admin" }).then(({ data: ok }) => {
          if (!ok) { toast.error("Not authorized as admin"); supabase.auth.signOut(); nav("/admin/auth", { replace: true }); }
          else setAuthorized(true);
        });
      }
    });
    return () => sub.subscription.unsubscribe();
  }, [nav]);

  async function logout() {
    await supabase.auth.signOut();
    nav("/admin/auth", { replace: true });
  }

  if (authorized === null) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading...</div>;

  return (
    <div className="min-h-screen bg-muted/30">
      <header className="sticky top-0 z-30 border-b border-border bg-background/90 backdrop-blur">
        <div className="container flex h-16 items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-primary text-primary-foreground font-black">G</div>
            <div>
              <div className="text-sm font-bold">Gurpreet Refrigeration</div>
              <div className="text-xs text-muted-foreground">Admin · {email}</div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" asChild>
              <Link to="/"><ExternalLink className="mr-1 h-4 w-4" /> View site</Link>
            </Button>
            <Button variant="ghost" size="sm" onClick={logout}><LogOut className="mr-1 h-4 w-4" /> Logout</Button>
          </div>
        </div>
      </header>

      <main className="container py-8">
        <Tabs defaultValue="leads">
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="leads">Leads</TabsTrigger>
            <TabsTrigger value="content">Content</TabsTrigger>
            <TabsTrigger value="navigation">Menu</TabsTrigger>
            <TabsTrigger value="services">Services</TabsTrigger>
            <TabsTrigger value="gallery">Gallery</TabsTrigger>
            <TabsTrigger value="testimonials">Testimonials</TabsTrigger>
          </TabsList>
          <TabsContent value="leads" className="mt-6"><LeadsTab /></TabsContent>
          <TabsContent value="content" className="mt-6"><ContentTab /></TabsContent>
          <TabsContent value="navigation" className="mt-6"><NavigationTab /></TabsContent>
          <TabsContent value="services" className="mt-6"><ServicesTab /></TabsContent>
          <TabsContent value="gallery" className="mt-6"><GalleryTab /></TabsContent>
          <TabsContent value="testimonials" className="mt-6"><TestimonialsTab /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
